import { useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { toast } from "react-toastify";
import { zenbridge } from "../api/zenbridgeClient";

const Sidebar = ({ setSelectedTenant, TenantsList, selectedTenant, fetchTenants }) => {
  const [addTenants, setAddTenants] = useState(false);
  const [tenantName, setTenantName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleAddTenant = async () => {
    if (!tenantName?.trim()) {
      toast.error("Please enter a tenant name");
      return;
    }

    setIsLoading(true);
    try {
      await zenbridge.post("/event_data_validator/tenant/new", {
        tenantId: tenantName.trim(),
        IsActive: true,
      });
      toast.success("Tenant created successfully!");
      setTenantName("");
      setAddTenants(false);
      fetchTenants();
    } catch (error) {
      console.error("Error creating tenant:", error);
      toast.error(error?.message || "Failed to create tenant");
    } finally {
      setIsLoading(false);
    }
  };

  const logoUrl = `${process.env.PUBLIC_URL}/ZenLogo.svg`;

  return (
    <div className="w-[280px] bg-white fixed top-0 left-0 h-full border-r border-gray-200 overflow-auto px-5 flex flex-col">
      <div className="flex items-center gap-3 py-5 border-b border-gray-200">
        <img src={logoUrl} alt="logo" className="h-10 w-10" />
        <h1 className="text-gray-800 font-semibold text-[16px]">SLA Monitor</h1>
      </div>

      <div className="flex flex-col justify-between h-full">
        <div className="pt-4">
          <h2 className="text-gray-500 text-[12px] font-medium uppercase mb-2">
            Customers
          </h2>
          <ul>
            {TenantsList ? (
              TenantsList.map((item, i) => (
                <li
                  key={i}
                  className={`px-3 py-2 text-[14px] font-normal cursor-pointer border-b transition-all ${
                    selectedTenant === item.TenantId
                      ? "bg-gray-100 text-gray-800"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                  onClick={() => setSelectedTenant(item.TenantId)}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="capitalize ml-1">{item.TenantId}</span>
                      <div
                        className={`${
                          item.AbnormalCount <= 0
                            ? " text-[#065F46]  border-[#065F46]"
                            : " text-[#DC2626]  border-[#DC2626]"
                        } text-xs font-medium flex justify-center items-center text-center p-1 rounded-full min-w-fit h-[20px] w-[32px]`}
                      >
                        {item.AbnormalCount}{" "}
                        <span className="text-gray-500 font-normal ml-1">
                          Abnormalities
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-0.5">
                      <div title="Event Hook Status">
                        {item.eventHookStatus === "Success" ? (
                          <span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="#10b981"
                              className="size-5"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                        ) : item.eventHookStatus === "NotSet" ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="#3b82f6"
                            className="size-5"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : item.eventHookStatus === "Warning" ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="#eab308"
                            className="size-5 "
                          >
                            <path
                              fillRule="evenodd"
                              d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          ""
                        )}
                      </div>

                      <div title="ZB Hook Status">
                        {item.zenbridgeHookStatus === "Success" ? (
                          <span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="#10b981"
                              className="size-5"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                        ) : item.zenbridgeHookStatus === "NotSet" ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="#3b82f6"
                            className="size-5"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : item.zenbridgeHookStatus === "Warning" ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="#eab308"
                            className="size-5 "
                          >
                            <path
                              fillRule="evenodd"
                              d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))
            ) : (
              <div className="px-4 text-gray-500 animate-pulse">
                Loading Customers...
              </div>
            )}
          </ul>
        </div>

        <div className="flex flex-col gap-1 mb-4 sticky bottom-0 justify-between border-t-2 pt-1.5 bg-white">
          <button
            onClick={() => setAddTenants(!addTenants)}
            title="Add Tenants"
            className="w-full flex items-center gap-2 px-2 py-1 text-gray-600 text-sm "
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            Add Tenants
          </button>
        </div>
      </div>

      {addTenants && (
        <Dialog open={addTenants} onClose={setAddTenants} className="relative z-10">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in "
          />

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <DialogPanel
                transition
                className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95 "
              >
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <DialogTitle
                      as="h3"
                      className="text-base font-semibold text-gray-900 flex gap-2 items-center"
                    >
                      Add New Tenant
                    </DialogTitle>
                    <div className="mt-4">
                      <label
                        htmlFor="tenantName"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Tenant Name
                      </label>
                      <input
                        type="text"
                        id="tenantName"
                        value={tenantName}
                        onChange={(e) => setTenantName(e.target.value)}
                        placeholder="Enter tenant name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleAddTenant}
                    disabled={isLoading}
                    className="inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold bg-[#7417B3] text-white hover:bg-[#610b9a] disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                  >
                    {isLoading ? "Adding..." : "Add Tenant"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAddTenants(false);
                      setTenantName("");
                    }}
                    disabled={isLoading}
                    className="mt-3 inline-flex w-full justify-center border rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring-1 inset-ring-gray-300 hover:bg-gray-50 disabled:opacity-50 sm:mt-0 sm:w-auto "
                  >
                    Cancel
                  </button>
                </div>
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default Sidebar;


